import { call, all, put, takeLatest, select, delay } from 'redux-saga/effects';

import axios from 'axios';
import { saveAs } from 'file-saver';

import { formatDateTime, formatMessage } from '~/i18n/translate';

import notification from '~/services/notification';
import clientVisitService from './client-visit-service';

import { ISA_ERROR, USER_ROLE } from '~/constants';

import * as clientVisitActions from './client-visit-actions';

import {
  GET_ALL_SELECTS_REQUEST,
  GET_ALL_REQUEST,
  GET_ID_MODAL_FEEDBACK_REQUEST,
  SHARE_CSV
} from './client-visit-constants';

import { ClientVisitFeedbackResponseInterface, ClientVisitInterface } from './client-visit-interface';

let cancel: any;
const CancelToken = axios.CancelToken;

export function* getAll({ filters }: any) {
  if (cancel !== undefined) cancel();
  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    const { page, rowsPerPage, order, orderBy } = yield select(state => state.clientVisit.list);

    const { externalCode } = yield select(state => state.auth.user);

    yield put(clientVisitActions.setLoadingData(true));

    const params = {
      userExternalCodes: filters?.vendors?.map(vendor => vendor?.externalCode)?.join(',') ?? '',
      clientExternalCodes: filters?.clients?.map(client => client?.externalCode)?.join(',') ?? '',
      presetJustificationInternalCode: filters?.justifications?.internalCode,
      beginDate: filters?.date
        ? new Date(filters?.date[0]?.toISOString().split('T')[0] + 'T00:00:00Z')
        : new Date(new Date().toISOString().split('T')[0] + 'T00:00:00Z'),
      endDate: filters?.date
        ? new Date(filters?.date[1]?.toISOString().split('T')[0] + 'T23:59:59Z')
        : new Date(new Date().toISOString().split('T')[0] + 'T23:59:59Z'),
      statusSaleFeedback: filters?.statusSaleFeedback
    };

    const pagination = {
      skip: page * rowsPerPage,
      limit: rowsPerPage,
      order,
      orderBy,
      orderAsc: order === 'asc' ?? 'asc'
    };

    const [{ data: allVisits }, { data: sellers }, { data: allVisitsCount }] = yield all([
      call(
        clientVisitService.getAllClientVisit,
        {
          ...params,
          ...pagination
        },
        { ...cancelToken }
      ),
      call(clientVisitService.getSellers, { ...cancelToken }),
      call(
        clientVisitService.getAllClientVisitCount,
        {
          ...params,
          ...pagination
        },
        { ...cancelToken }
      )
    ]);

    const parsedVisitsData: ClientVisitInterface = allVisits?.map(item => ({
      ...item,
      vendor: sellers?.find(seller => seller?.externalCode === item?.userExternalCode)
    }));

    yield put(
      clientVisitActions.getAllSuccess({
        rows: parsedVisitsData,
        selects: {
          vendors: sellers?.filter(seller => seller?.userRole === USER_ROLE?.VENDOR)
        },
        rowsCount: allVisitsCount?.count || 0
      })
    );

    yield put(clientVisitActions.setLoadingData(false));
  } catch (error) {
    yield put(clientVisitActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getAllSelects({ client }: any) {
  yield delay(200);

  if (cancel !== undefined) cancel();
  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    yield put(clientVisitActions.setLoadingSelects(true));

    const { externalCode } = yield select(state => state.auth.user);

    const [{ data: sellers }, { data: clients }, { data: justifications }] = yield all([
      call(clientVisitService.getSellers, { ...cancelToken }),
      call(
        clientVisitService.getClients,
        {
          userExternalCode: externalCode,
          queryString: client ?? ''
        },
        { ...cancelToken }
      ),
      call(clientVisitService.getJustifications, { ...cancelToken })
    ]);

    yield put(
      clientVisitActions.getAllSelectsSuccess({
        selects: {
          vendors: sellers?.filter(seller => seller?.userRole === USER_ROLE?.VENDOR),
          clients,
          justifications
        }
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
    yield put(clientVisitActions.setLoadingSelects(false));
  } finally {
    yield put(clientVisitActions.setLoadingSelects(false));
  }
}

export function* getClientVisitFeedbacks({ client }: any) {
  try {
    yield put(clientVisitActions.setLoadingModalFeedback(true));

    if (!client) {
      return;
    }

    const { data: feedbacks } = yield call(clientVisitService.getClientVisitFeedbacks, {
      id: client?.id
    });

    const parsedFeedbacks: ClientVisitFeedbackResponseInterface = feedbacks?.map(item => ({
      ...item,
      clientName: client?.clientName,
      name: client?.name
    }));

    yield put(clientVisitActions.getModalFeedbackSuccess(parsedFeedbacks));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
    yield put(clientVisitActions.setLoadingModalFeedback(false));
  } finally {
    yield put(clientVisitActions.setLoadingModalFeedback(false));
  }
}

export function* shareCsv({ exportType, filters }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  try {
    const params = {
      userExternalCodes: filters?.vendors?.map(vendor => vendor?.externalCode)?.join(',') ?? '',
      clientExternalCodes: filters?.clients?.map(client => client?.externalCode)?.join(',') ?? '',
      beginDate: filters?.date
        ? new Date(filters?.date[0]?.toISOString().split('T')[0] + 'T00:00:00Z')
        : new Date(new Date().toISOString().split('T')[0] + 'T00:00:00Z'),
      endDate: filters?.date
        ? new Date(filters?.date[1]?.toISOString().split('T')[0] + 'T23:59:59Z')
        : new Date(new Date().toISOString().split('T')[0] + 'T23:59:59Z'),
      presetJustificationInternalCode: filters?.justifications?.internalCode,
      statusSaleFeedback: filters?.statusSaleFeedback
    };

    if (exportType === 'xlsx') {
      const { data: csv } = yield call(clientVisitService.getXls, params);

      const blob = new Blob([csv], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, `Visitas - ${formatDateTime(new Date())}.xlsx`);
    } else {
      const { data: csv } = yield call(clientVisitService.getCSV, params);

      const reader = new FileReader();

      const blobFile = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

      reader.readAsDataURL(blobFile);
      reader.onloadend = async function () {
        saveAs(blobFile, `Visitas - ${formatDateTime(new Date())}.csv`);
      };
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_ALL_SELECTS_REQUEST, getAllSelects),
  takeLatest(GET_ID_MODAL_FEEDBACK_REQUEST, getClientVisitFeedbacks),
  takeLatest(SHARE_CSV, shareCsv)
]);
